import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion, AnimatePresence } from "framer-motion";
import { CoeService } from "../../../services/dataService";

/* ─── Icons ─── */
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const ChevronDown = ({ className = "", rotated = false }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${rotated ? 'rotate-180' : ''} ${className}`}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const DotsVert = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
  </svg>
);
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const UnlockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const FolderIcon = () => (
  <svg width="40" height="34" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.5 4H4C2.9 4 2 4.9 2 6V28C2 29.1 2.9 30 4 30H36C37.1 30 38 29.1 38 28V10C38 8.9 37.1 8 36 8H20.5L16.5 4Z" fill="#a4b4f5" />
    <path d="M36 10H20.5L16.5 6H4C2.9 6 2 6.9 2 8V28C2 29.1 2.9 30 4 30H36C37.1 30 38 29.1 38 28V12C38 10.9 37.1 10 36 10Z" fill="#748bf7" />
  </svg>
);

/* ─── Portal Dropdown ─── */
const TailDropdown = ({ open, onClose, children, className = "", triggerElement }) => {
  const ref = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open && triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const isRightSide = rect.left > window.innerWidth / 2;
      if (isRightSide) {
        setCoords({ top: rect.bottom + scrollY, right: window.innerWidth - (rect.right + scrollX), left: "auto" });
      } else {
        setCoords({ top: rect.bottom + scrollY, left: rect.left + scrollX, right: "auto" });
      }
    }
  }, [open, triggerElement]);

  if (!open) return null;
  return createPortal(
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -6 }}
      transition={{ duration: 0.12 }}
      style={{ position: "absolute", top: (coords.top || 0) + 5, left: coords.left, right: coords.right, zIndex: 99999, minWidth: "160px" }}
      className={`bg-white rounded-xl border border-slate-100 shadow-2xl overflow-hidden ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </motion.div>,
    document.body
  );
};

const DropItem = ({ icon, label, color = "text-slate-600", hoverBg = "hover:bg-slate-50", onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider ${color} ${hoverBg} transition-colors border-0 bg-transparent`}>
    <span className="flex-shrink-0 flex items-center justify-center w-4 h-4">{icon}</span>
    {label}
  </button>
);

/* ─── Modal ─── */
const Modal = ({ open, onClose, title, children }) => (
  <AnimatePresence>
    {open && (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, scale: 0.95, y: -8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
          <div className="h-1 w-full bg-gradient-to-r from-slate-700 to-blue-600" />
          <div className="flex items-center justify-between px-6 pt-5 pb-2">
            <h2 className="text-[15px] font-extrabold text-slate-800">{title}</h2>
            <button onClick={onClose} className="w-7 h-7 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"><XIcon /></button>
          </div>
          <div className="px-6 pb-6 pt-3">{children}</div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

/* ─── Main Component ─── */
const CoeAdminDomains = () => {
  const { academyId } = useParams();
  const navigate = useNavigate();

  const [corporateName, setCorporateName] = useState("Corporate Academy");
  const [allDomains, setAllDomains] = useState([]);
  const [availableDomains, setAvailableDomains] = useState([]);
  const [coeGroups, setCoeGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDomains, setSelectedDomains] = useState([]);

  // Create Domain modal
  const [createDomainOpen, setCreateDomainOpen] = useState(false);
  const [newDomainName, setNewDomainName] = useState("");
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Dropdown state — per-key trigger map
  const [actionOpen, setActionOpen] = useState({});
  const [menuTriggerElMap, setMenuTriggerElMap] = useState({});
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Access control optimistic map { coe_id: is_active }
  const [accessMap, setAccessMap] = useState({});

  useEffect(() => { fetchData(); }, [academyId]);


  const getCorporateKeyword = (name) => {
    const n = name?.toLowerCase() || "";
    if (n.includes("aws") || n.includes("amazon")) return "aws";
    if (n.includes("juniper")) return "juniper";
    if (n.includes("palo alto") || n.includes("paloalto")) return "palo alto";
    if (n.includes("blue prism") || n.includes("blueprism")) return "blue prism";
    if (n.includes("alteryx")) return "alteryx";
    if (n.includes("celonis")) return "celonis";
    if (n.includes("fortinet")) return "fortinet";
    if (n.includes("google")) return "google";
    if (n.includes("microchip")) return "microchip";
    if (n.includes("uipath")) return "uipath";
    if (n.includes("zscaler")) return "zscaler";
    if (n.includes("altair")) return "altair";
    if (n.includes("ansys")) return "ansys";
    if (n.includes("bentley")) return "bentley";
    if (n.includes("midas")) return "midas";
    if (n.includes("wadhwani")) return "wadhwani";
    if (n.includes("eduskills")) return "eduskills";
    return n.split(" ")[0]; // fallback
  };

  // Lazy-load available domains only when the user opens the Create Domain modal
  const fetchAvailableDomains = async () => {
    try {
      const res = await CoeService.getAvailableGlobalDomains(academyId);
      if (res?.data) {
        setAvailableDomains(res.data.data || res.data);
      }
    } catch (err) {
      console.error("Failed to load available domains", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      // Only fire the 3 essential calls on page load (global domains is lazy-loaded on button click)
      const [corpResult, domainsResult, docsResult] = await Promise.allSettled([
        CoeService.getActiveAcademies(),
        CoeService.getAllDomainsByCorporate(academyId),
        CoeService.getDomainCards(academyId),   // Backend filters & groups coe_content only
      ]);

      if (corpResult.status === "fulfilled" && corpResult.value?.data) {
        const academies = Array.isArray(corpResult.value.data) ? corpResult.value.data : [];
        const corp = academies.find(c => String(c.academy_id) === String(academyId));
        if (corp) setCorporateName(corp.academy_brand || corp.academy_name || "Corporate Academy");
      }

      if (domainsResult.status === "fulfilled" && domainsResult.value?.data) {
        setAllDomains(domainsResult.value.data);
      }

      if (docsResult.status === "fulfilled" && docsResult.value?.data) {
        // Backend already returns grouped coe_content data — no frontend filtering needed
        const groupedList = Array.isArray(docsResult.value.data) ? docsResult.value.data : [];

        // Seed access map from backend data
        const initAccess = {};
        groupedList.forEach(group => {
          group.entries.forEach(entry => {
            initAccess[entry.coe_domain_id] = entry.is_active !== false;
          });
        });
        setAccessMap(prev => ({ ...initAccess, ...prev }));

        // Convert list format [{ title, entries }] → object { title: entries[] }
        const groups = {};
        groupedList.forEach(group => {
          groups[group.title] = group.entries;
        });
        setCoeGroups(groups);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDomain = async () => {
    if (selectedDomains.length === 0) return alert("Please select at least one domain.");
    setCreatingDomain(true);
    try {
      await Promise.all(selectedDomains.map(async (domain) => {
        // Send the ORIGINAL domain_id to the backend.
        // The backend inserts into coe_domains (not domains table).
        const formData = new FormData();
        formData.append("domain_name", domain.domain_name.trim());
        formData.append("domain_id", domain.domain_id);
        await CoeService.addDomain(academyId, formData);
      }));

      setCreateDomainOpen(false);
      setSelectedDomains([]);
      setNewDomainName("");
      fetchData();
    } catch (e) {
      alert("Failed to create domains: " + (e.response?.data?.detail || e.message));
    } finally {
      setCreatingDomain(false);
    }
  };

  const handleDelete = async (coeId, coeName) => {
    closeAction(coeId);
    if (!window.confirm(`Delete "${coeName}"? This will remove the domain card.`)) return;
    try {
      await CoeService.deleteCoeDomain(coeId);
      fetchData();
    } catch (err) {
      alert("Failed to delete domain: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleAccess = async (entry) => {
    const currentAccess = accessMap[entry.coe_domain_id] !== undefined ? accessMap[entry.coe_domain_id] : (entry.is_active !== false);
    const newAccess = !currentAccess;
    setAccessMap(prev => ({ ...prev, [entry.coe_domain_id]: newAccess }));
    closeAction(entry.coe_domain_id);
    try {
      await CoeService.toggleCoeDomainAccess(entry.coe_domain_id);
    } catch {
      setAccessMap(prev => ({ ...prev, [entry.coe_domain_id]: currentAccess }));
    }
  };

  const toggleAction = (key, targetElement) => {
    setMenuTriggerElMap(prev => ({ ...prev, [key]: targetElement }));
    setActionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const closeAction = (key) => setActionOpen(prev => ({ ...prev, [key]: false }));

  const coeCount = Object.keys(coeGroups).length;
  const domainCount = Object.values(coeGroups).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="min-h-screen bg-slate-50 font-sans p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Tooltip title="Back to Corporates">
              <IconButton onClick={() => navigate("/admin-coe")} sx={{ bgcolor: "white", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f8fafc" } }}>
                <ArrowBackIcon sx={{ fontSize: 18, color: "#2563eb" }} />
              </IconButton>
            </Tooltip>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight leading-none">{corporateName}</h1>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1.5">COE Management</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex gap-3">
              {[
                { label: "Total Domains", value: loading ? "..." : domainCount },
              ].map(stat => (
                <div key={stat.label} className="bg-white border border-slate-100 rounded-2xl px-5 py-2 flex flex-col min-w-[120px] shadow-sm">
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{stat.label}</span>
                  <span className="text-xl font-black text-slate-800 leading-tight mt-0.5">{stat.value}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => { setCreateDomainOpen(true); setSelectedDomains([]); setNewDomainName(""); setShowSuggestions(false); fetchAvailableDomains(); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-bold text-[11px] uppercase tracking-widest shadow-sm transition-all h-[52px]"
            >
              <PlusIcon /> Create Domain
            </button>
          </div>
        </div>

        {/* ── COE Cards ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <CircularProgress size={32} thickness={5} sx={{ color: "#2563eb" }} />
            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest animate-pulse">Loading COEs...</p>
          </div>
        ) : coeCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 bg-white border border-dashed border-slate-200 rounded-3xl">
            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center">
              <FolderIcon />
            </div>
            <h3 className="text-lg font-black text-slate-600">No COEs yet</h3>
            <p className="text-slate-400 text-sm font-medium text-center max-w-xs">Click "Create Domain" to start managing documents for {corporateName}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Object.entries(coeGroups).flatMap(([coeName, domains]) =>
              domains.map((entry, idx) => {
                const isOn = accessMap[entry.coe_domain_id] !== undefined ? accessMap[entry.coe_domain_id] : (entry.is_active !== false);
                return (
                  <motion.div
                    key={entry.coe_domain_id + "-" + entry.domain_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    whileHover={{ y: -4 }}
                    className={`group cursor-pointer bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all flex flex-col p-5 relative ${!isOn ? 'border-red-200 opacity-70' : 'border-slate-200 hover:border-blue-200'}`}
                    onClick={() => navigate(`/admin-coe/${academyId}/${entry.domain_id}`, { state: { domainName: entry.domain_name } })}
                  >
                    {/* Access badge */}
                    {!isOn && (
                      <div className="absolute top-2 left-2 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <LockIcon />
                        <span className="text-[8px] font-bold text-red-500 uppercase tracking-wider">Hidden</span>
                      </div>
                    )}

                    <div className="mb-4 flex justify-between items-start">
                      <FolderIcon />
                      {/* 3-dot menu */}
                      <div className="relative" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={(e) => toggleAction(entry.coe_domain_id, e.currentTarget)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors -mt-1 -mr-1"
                        >
                          <DotsVert />
                        </button>
                        <TailDropdown
                          open={!!actionOpen[entry.coe_domain_id]}
                          onClose={() => closeAction(entry.coe_domain_id)}
                          triggerElement={menuTriggerElMap[entry.coe_domain_id]}
                          className="w-44"
                        >
                          <div className="py-1.5">
                            <DropItem
                              icon={<TrashIcon />}
                              label="Delete"
                              color="text-red-500"
                              hoverBg="hover:bg-red-50"
                              onClick={() => handleDelete(entry.coe_domain_id, coeName)}
                            />
                          </div>
                        </TailDropdown>
                      </div>
                    </div>

                    <div>
                      <p className="text-[15px] font-[750] text-slate-800 truncate mb-1">{entry.domain_name.replace(/\s*#\d+$/, '')}</p>
                      <p className="text-[12px] font-bold text-slate-600 truncate">{corporateName}</p>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

      </motion.div>

      {/* ── Create Domain Modal ── */}
      <Modal open={createDomainOpen} onClose={() => !creatingDomain && setCreateDomainOpen(false)} title="Create New Domain">
        <div className="space-y-5">
          {/* Input field */}
          <div className="relative" ref={suggestionsRef}>
            <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-2">Domain Name</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </div>
              <input
                type="text"
                value={newDomainName}
                onFocus={() => setShowSuggestions(true)}
                onChange={e => { setNewDomainName(e.target.value); setShowSuggestions(true); }}
                onKeyDown={e => { if (e.key === "Enter" && !creatingDomain) handleCreateDomain(); }}
                placeholder="Search or type a new domain..."
                className={`w-full bg-slate-50/50 border border-slate-200 pl-11 pr-12 py-3.5 text-sm font-bold text-slate-700 focus:ring-4 focus:ring-blue-50 focus:border-blue-300 focus:bg-white outline-none transition-all placeholder:text-slate-300 ${showSuggestions ? "rounded-t-2xl rounded-b-none border-b-0" : "rounded-2xl"}`}
              />
              <button
                type="button"
                onClick={() => setShowSuggestions(!showSuggestions)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-blue-500 hover:bg-blue-50 transition-all"
              >
                <ChevronDown rotated={showSuggestions} />
              </button>
            </div>

            {/* Domain Suggestions Dropdown */}
            {showSuggestions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.15 }}
                className="absolute z-[1001] w-full bg-white border border-slate-200 border-t-0 rounded-b-2xl shadow-lg overflow-hidden"
              >
                <div className="max-h-64 overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
                  {(() => {
                    const apiDomains = Array.isArray(availableDomains) ? availableDomains : [];

                    // Filter by what the user typed in the search box
                    const filtered = apiDomains.filter(d =>
                      d.domain_name?.toLowerCase().includes(newDomainName.toLowerCase()) ||
                      d.clean_name?.toLowerCase().includes(newDomainName.toLowerCase())
                    );

                    if (filtered.length === 0) return (
                      <div className="flex flex-col">
                        <div className="px-4 py-4 text-[10px] font-bold text-slate-300 uppercase tracking-wider text-center">No existing domains found</div>
                      </div>
                    );
                    return (
                      <>
                        <div className="px-4 py-2 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Available Domains</span>
                          <span className="text-[9px] font-black text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">{filtered.length}</span>
                        </div>
                        {filtered.map((d, i) => {
                          const dId = d.domain_id || d.id;
                          const isSelected = selectedDomains.some(sd => (sd.domain_id || sd.id) === dId);
                          return (
                            <button
                              key={dId}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedDomains(prev => prev.filter(sd => (sd.domain_id || sd.id) !== dId));
                                } else {
                                  setSelectedDomains(prev => [...prev, d]);
                                }
                                setNewDomainName("");
                                suggestionsRef.current?.querySelector('input')?.focus();
                              }}
                              className={`w-full px-4 py-2.5 text-left text-[12px] font-semibold transition-all flex items-center justify-between group/item ${i < filtered.length - 1 ? "border-b border-slate-50" : ""} ${isSelected ? "bg-blue-50/50 text-blue-700" : "text-slate-600 hover:bg-slate-50"}`}
                            >
                              <div className="flex items-center gap-2.5">
                                <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors ${isSelected ? "bg-blue-500 border-blue-500" : "border-slate-300 group-hover/item:border-blue-400 bg-white"}`}>
                                  {isSelected && <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                                </div>
                                <span className="truncate">{d.domain_name.replace(/\s*#\d+$/, '')}</span>
                              </div>
                            </button>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </motion.div>
            )}
          </div>

          {/* Selected Chips */}
          {selectedDomains.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {selectedDomains.map(d => {
                const dId = d.domain_id || d.id;
                return (
                  <div key={dId} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
                    <span className="text-[11px] font-bold text-blue-700">{d.domain_name.replace(/\s*#\d+$/, '')}</span>
                    <button
                      onClick={() => setSelectedDomains(prev => prev.filter(sd => (sd.domain_id || sd.id) !== dId))}
                      className="w-4 h-4 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center hover:bg-blue-200 hover:text-blue-700 transition-colors"
                    >
                      <XIcon />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setCreateDomainOpen(false)}
              disabled={creatingDomain}
              className="flex-1 py-3.5 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-600 transition-all active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateDomain}
              disabled={creatingDomain || selectedDomains.length === 0}
              className={`flex-1 py-3.5 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] transition-all shadow-xl bg-blue-600 text-white ${creatingDomain || selectedDomains.length === 0 ? "cursor-not-allowed shadow-none opacity-80" : "hover:bg-blue-700 hover:shadow-blue-200 hover:scale-[1.02] active:scale-95"}`}
            >
              {creatingDomain ? "Creating..." : "Create Domain"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CoeAdminDomains;
